import React from 'react';
import styled from 'styled-components';
import { Navbar } from '@pages/splash/Navbar';
import { Link } from 'react-router-dom';
import { Body } from '@ui';
import { Body1, H5, Container, LoadingSpinner, Body5, H1, Body6, Page } from '@ui';
import { createTimeInstance, formatLocalizedDateTime } from '@utils/time';
import { CityScape } from '@ui/CityScape';
import { getFromCMSApi } from '@services/api';
import useWindowSize from '@hooks/application/useWindowSize';

const yellow = '#fcff7d';
const skylineColor = yellow;
const Wrapper = styled.div`
  min-height: 70vh;
  padding: 0 40px 24px 40px;
  margin: 0 auto;
  position: relative;
  max-width: 1440px;

  @media (min-width: 1440px) {
    padding-top: 24px;
  }

  @media (max-width: 768px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: 420px) {
    padding-left: 8px;
    padding-right: 8px;
  }
`;
const TagWrapper = styled.div`
  border-radius: 20px;
  padding: 2px 10px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};

  * {
    font-size: 12px !important;
    line-height: 1.2 !important;
  }
`;
const TagList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  gap: 8px;
  flex-wrap: wrap;
  padding-right: 4px;
`;
const BackgroundWrapper = styled.div`
  z-index: 1;
`;
const ImageWrapper = styled.div`
  position: absolute;
  top: -80px;
  left: 0;

  height: 580px;
  width: 100vw;

  svg {
    height: 560px !important;
    width: auto !important;
  }

  path {
    fill: ${({ theme }) => skylineColor} !important;
  }

  z-index: 13;
  pointer-events: none;
  overflow: hidden;
`;
const FirstImage = styled.div`
  position: absolute;
  z-index: 1;
`;
const SecondImage = styled.div`
  position: absolute;
  z-index: 1;
  left: 566px;
`;
const ThirdImage = styled.div`
  position: absolute;
  z-index: 1;
  left: 1132px;
`;
const ForthImage = styled.div`
  position: absolute;
  z-index: 1;
  left: 1698px;
`;
const SubHeadingWrapper = styled.div`
  padding-top: 4px;

  * {
    font-size: 24px;
    line-height: 36px;
  }
`;
const HeadingContentWrapper = styled.div`
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 5px;

  padding: 36px;

  h1 {
    font-weight: 100;

    b {
      font-weight: 600;
    }
  }
`;
const HeadingWrapper = styled.div`
  position: relative;
  border-radius: 2px;
  height: 400px;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 0 40px;
  margin: 0 -40px;

  background: #222;

  @media (max-width: 1000px) {
    h1,
    h1 b {
      font-size: 48px;
      line-height: 1.4;
    }

    ${SubHeadingWrapper} {
      max-width: 370px;
      * {
        font-size: 18px;
        line-height: 28px;
      }
    }
  }
  @media (max-width: 768px) {
    padding-left: 52px;
    padding-right: 52px;
  }
  @media (max-width: 550px) {
    h1,
    h1 b {
      font-size: 36px;
      line-height: 1.2;
    }

    ${SubHeadingWrapper} {
      max-width: 330px;
      * {
        font-size: 16px;
        line-height: 28px;
      }
    }
  }
`;
const LoadingWrapper = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const BlogPostsList = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 900px;
`;
const BlogPostPreviewLinkWrapper = styled.div`
  a {
    text-decoration: none;
  }
`;
const BlogTitleText = styled.div``;
const BlogDescriptionText = styled.div``;
const BlogPublishedDateText = styled.div``;
const PreviewTextContent = styled.div`
  flex: 1;
  padding-top: 8px;
  padding-right: 24px;

  @media (max-width: 640px) {
    ${BlogTitleText} {
      * {
        font-size: 16px;
        line-height: 20px;
      }
    }
    ${BlogDescriptionText} {
      width: 100%;

      * {
        font-size: 12px;
        line-height: 16px;
      }
    }
    ${BlogPublishedDateText} {
      * {
        font-size: 12px !important;
        line-height: 1.2;
      }
    }
  }

  @media (max-width: 500px) {
    width: 100%;
    min-width: 100%;
    max-width: unset;
  }
`;
const PreviewImageWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;

  width: 35%;
  max-width: 260px;
  overflow: hidden;

  img {
    width: 100%;
  }

  @media (max-width: 640px) {
    min-width: 205px;
  }
`;
const BlogPostPreviewWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  padding: 24px 36px 24px 18px;
  margin: 0 -20px;

  min-height: 200px;

  &:hover {
    @media (min-width: 773px) {
      transform: scale(1.01);
      background: #fff;
      border-radius: 5px;
      box-shadow: 0 3px 9px rgba(0, 0, 0, 0.1);

      transition: all 150ms;

      * {
        opacity: 0.98;
      }
    }
  }

  @media (min-width: 1440px) {
    margin: 0 -32px;
    padding: 24px 32px;
  }

  @media (max-width: 768px) {
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 32px;

    min-height: 160px;

    ${PreviewTextContent} {
      min-width: unset;
      max-width: unset;
      flex: 2.5;
    }
    ${PreviewImageWrapper} {
      min-width: unset;
      max-width: unset;
      width: 80px;
    }
  }

  @media (max-width: 500px) {
    min-height: 120px;

    ${TagList} {
      display: none;
    }

    ${PreviewTextContent} {
      min-width: unset;
      max-width: unset;
      flex: 2.5;
    }
    ${PreviewImageWrapper} {
      min-width: unset;
      max-width: unset;
      width: 80px;
    }
  }

  @media (max-width: 376px) {
    display: block;
    padding-left: 20px;

    ${BlogDescriptionText} {
      display: none;
    }

    ${PreviewImageWrapper} {
      position: relative;
      max-height: unset;
      padding-top: 36px;
      min-width: 104vw;
      margin: -16px;
      overflow: hidden;

      img {
        width: 100%;
      }
    }
  }
`;
const BlogPostListWrapper = styled.div`
  padding-top: 24px;
`;

export const getBlogPosts = async () => {
  const responseData = await getFromCMSApi({
    dir: '/blogs?populate=*&sort[1]=pinned_at%3Adesc&sort[2]=first_published_at%3Adesc&pagination[page]=1&pagination[pageSize]=100',
  });
  return responseData;
};

const getTimeValue = (v) => {
  if (!v) return null;
  return createTimeInstance(v).valueOf();
};

const useSortedBlogList = () => {
  const [blogs, setBlogs] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [wasError, setWasError] = React.useState(false);

  const sort = (blogs) => {
    const pinnedBlogs = blogs
      .filter((b) => !!b.attributes.pinned_at)
      .sort((a, b) => getTimeValue(b.attributes.pinned_at) - getTimeValue(a.attributes.pinned_at));
    const notPinnedBlogs = blogs
      .filter((b) => !b.attributes.pinned_at)
      .sort((a, b) => getTimeValue(b.attributes.first_published_at) - getTimeValue(a.attributes.first_published_at));
    return [...pinnedBlogs, ...notPinnedBlogs];
  };

  React.useEffect(() => {
    const load = async () => {
      const { data, meta, error } = await getBlogPosts();
      if (error) {
        setWasError(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setBlogs(sort(data));
      }
    };
    load();
  }, []);

  return {
    list: blogs,
    isLoading,
    wasError,
  };
};

const PreviewImage = ({ imageData }) => {
  if (!imageData || !imageData.data) {
    return null;
  }

  const {
    data: { attributes },
  } = imageData;
  const { url } = attributes;

  return (
    <PreviewImageWrapper>
      <img src={url} alt={'header-img'} />
    </PreviewImageWrapper>
  );
};

const Tag = ({ data }) => {
  const {
    attributes: { tag_name },
  } = data;
  const displayString = tag_name
    .split(' ')
    .map((s) => s[0].toUpperCase() + s.slice(1, s.length))
    .join(' ');
  return (
    <TagWrapper>
      <Body5>{displayString}</Body5>
    </TagWrapper>
  );
};

const Tags = ({ list }) => {
  if (!list || list.length === 0) {
    return null;
  }
  return (
    <TagList>
      {list.map((el, i) => (
        <Tag key={i} data={el} />
      ))}
    </TagList>
  );
};

const BlogPostPreview = ({ data }) => {
  const { attributes, id } = data;
  const { title, description, header, slug, first_published_at, tags } = attributes;
  const timeDisplay = formatLocalizedDateTime('ll2', createTimeInstance(first_published_at));

  return (
    <BlogPostPreviewLinkWrapper>
      <Link to={`/blog/${slug}`}>
        <BlogPostPreviewWrapper>
          <PreviewTextContent>
            <BlogTitleText>
              <Container top={0}>
                <H5>{title}</H5>
              </Container>
            </BlogTitleText>

            <Container top={6} row verticallyCenter flexWrap gap={8}>
              <BlogPublishedDateText>
                <Body5 isLowContrast>{timeDisplay}</Body5>
              </BlogPublishedDateText>
            </Container>
            <Container top={16} row verticallyCenter flexWrap gap={8}>
              <Tags list={tags.data} />
            </Container>
          </PreviewTextContent>
          <PreviewImage imageData={header} />
        </BlogPostPreviewWrapper>
      </Link>
    </BlogPostPreviewLinkWrapper>
  );
};

const BlogPosts = () => {
  const { list: blogs, isLoading, wasError } = useSortedBlogList();

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Container top={76}>
            <LoadingSpinner />
          </Container>
        </LoadingWrapper>
      </Container>
    );
  }

  if (!blogs || wasError) {
    return (
      <div>
        <Container top={16}>
          <Body1>Check back later for more blog posts.</Body1>
        </Container>
      </div>
    );
  }

  return (
    <BlogPostsList>
      {blogs?.map((b) => (
        <BlogPostPreview key={b.attributes.title} data={b} />
      ))}
    </BlogPostsList>
  );
};

const calcTop = (windowHeight) => {
  const imageHeight = 800;
  const value = (windowHeight - imageHeight) / 2;
  return value - value * 0.15;
};

const BackgroundImage = () => {
  const { height: windowHeight, width: windowWidth } = useWindowSize();
  return (
    <ImageWrapper Top={calcTop(windowHeight)} className={'skyline-wrap'}>
      <FirstImage>
        <CityScape />
      </FirstImage>
      <SecondImage>
        <CityScape />
      </SecondImage>
      {windowWidth > 1100 && (
        <ThirdImage>
          <CityScape />
        </ThirdImage>
      )}
      {windowWidth > 1500 && (
        <ForthImage>
          <CityScape />
        </ForthImage>
      )}
    </ImageWrapper>
  );
};

export default function Blog() {
  return (
    <>
      <Navbar />
      <Body>
        <Page>
          <Wrapper>
            <HeadingWrapper>
              <BackgroundWrapper>
                <BackgroundImage />
              </BackgroundWrapper>
              <HeadingContentWrapper>
                <H1 uppercase>
                  <b>The Tornado</b> Blog
                </H1>
                <SubHeadingWrapper>
                  <Body6>AI for elite alpha generation and deal flow</Body6>
                </SubHeadingWrapper>
              </HeadingContentWrapper>
            </HeadingWrapper>
            <BlogPostListWrapper>
              <BlogPosts />
            </BlogPostListWrapper>
            <Container top={48} />
          </Wrapper>
        </Page>
      </Body>
    </>
  );
}
